import {
    Divider,
    Grid,
    Button as MuiButton,
    Paper,
    makeStyles,
} from '@material-ui/core/';
import AddIcon from '@mui/icons-material/Add';
import * as React from 'react';
import { useEffect } from 'react';
import type { Avatar } from '../actions/getInitData';
import { sendRequest } from '../api/websocketApi';
import dashboardBackground from '../assets/dashboardBackground.png';
import AvatarImage from '../components/AvatarImage';
import BarChart from '../components/BarChart';
import Button from '../components/Button';
import CustomTooltip from '../components/CustomTooltip';
import DoughnutChart, { ChartDataItem } from '../components/DoughnutChart';
import EditMemberForm from '../components/EditMemberForm';
import EditProfileForm from '../components/EditProfileForm';
import TestName from '../components/TestName';
import theme from '../consts/Theme';
import { ReactComponent as EditIcon } from '../icons/edit.svg';
import useStore from '../store/store';

interface Event {
    Event: 'KitRejected' | 'KitReleased' | 'KitOrdered' | 'MemberAdded';
    SampleId: string | null;
    PatientId: string | null;
    Date: Date;
}

interface LatestKits {
    Type: string;
    SampleId: string | null;
    PatientId: string | null;
}

interface MonthlyCount {
    Date: Date;
    Count: number;
}

interface StatusCounts {
    Ordered: number;
    Pending: number;
    Rejected: number;
    Released: number;
}

const DashboardPage = () => {
    const setTitle = useStore((state) => state.setTitle);
    const classes = useStyles({});
    const setFormError = useStore((state) => state.setFormError);
    const profile = useStore((state) => state.profile);
    const defaultAvatars = useStore((state) => state.defaultAvatars);
    const [latestEvents, setLatestEvents] = React.useState<Event[]>([]);
    const [latestReleasedKits, setLatestReleasedKits] = React.useState<
        LatestKits[]
    >([]);
    const [latestPendingKits, setLatestPendingKits] = React.useState<
        LatestKits[]
    >([]);
    const [monthlyMemberCounts, setMonthlyMemberCounts] = React.useState<
        MonthlyCount[]
    >([]);
    const [monthlyReleasedKitCounts, setMonthlyReleasedKitCounts] =
        React.useState<MonthlyCount[]>([]);
    const [statusCounts, setstatusCounts] = React.useState<StatusCounts>({
        Ordered: 0,
        Pending: 0,
        Rejected: 0,
        Released: 0,
    });
    const isNoTestPerformanceData =
        statusCounts.Ordered === 0 &&
        statusCounts.Pending === 0 &&
        statusCounts.Rejected === 0 &&
        statusCounts.Released === 0;
    const [newMemberDialogOpen, setNewMemberDialogOpen] = React.useState(false);
    const [editProfileDialogOpen, setEditProfileDialogOpen] =
        React.useState(false);
    const [memberAdded, setMemberAdded] = React.useState(false);

    const parsedAvatarId = Number.parseInt(profile.avatarId ?? '', 10);

    const avatarPath =
        defaultAvatars.find(
            (avatar: Avatar) => avatar.DefaultAvatarId === parsedAvatarId
        )?.FilePath || defaultAvatars[0]?.FilePath;

    const handleNewMemberClick = () => setNewMemberDialogOpen(true);
    const handleNewMemberDialogClose = () => setNewMemberDialogOpen(false);

    useEffect(() => {
        setTitle('Genome Portal - Dashboard');
    }, [setTitle]);

    useEffect(() => {
        interface APIResponse {
            success: boolean;
            latestEvents: Event[];
            latestReleasedKits: LatestKits[];
            latestPendingKits: LatestKits[];
            monthlyMemberCounts: MonthlyCount[];
            monthlyOrderedKitCounts: MonthlyCount[];
            monthlyReleasedKitCounts: MonthlyCount[];
            statusCounts: StatusCounts;
            errCode?: 'invalid_token' | 'auth_failed' | 'access_denied';
            errDetails?: string;
        }
        sendRequest(
            {
                endpoint: '/dashboard/get-statistics',
            },
            (response: APIResponse) => {
                const {
                    success,
                    latestEvents,
                    latestReleasedKits,
                    latestPendingKits,
                    monthlyMemberCounts,
                    monthlyReleasedKitCounts,
                    statusCounts,
                    errCode,
                    errDetails,
                } = response;
                if (success) {
                    setLatestEvents(latestEvents);
                    setLatestReleasedKits(latestReleasedKits);
                    setLatestPendingKits(latestPendingKits);
                    setMonthlyMemberCounts(monthlyMemberCounts);
                    setMonthlyReleasedKitCounts(monthlyReleasedKitCounts);
                    setstatusCounts(statusCounts);
                } else if (errCode) {
                    console.error(errCode);
                    setFormError('');
                    setTimeout(() => setFormError(errCode), 0);
                    if (errDetails) console.error(errDetails);
                }
            }
        );
    }, [setFormError, memberAdded]);

    const getStatusFromEvent = (event: Event): string => {
        if (event.Event === 'KitRejected') return 'Rejected';
        if (event.Event === 'KitReleased') return 'Released';
        if (event.Event === 'KitOrdered') return 'Ordered';
        return 'Pending';
    };

    return (
        <>
            <div className={classes.wrapper}>
                <div className={classes.backgroundImage}></div>
                <div className={classes.root}>
                    <Grid
                        container
                        spacing={3}
                        className={classes.grid}
                        alignItems="stretch"
                    >
                        <Grid item xs={8}>
                            <Paper elevation={0} className={classes.header}>
                                <div className={classes.headerContainer}>
                                    <div
                                        className={classes.headerImageContainer}
                                    >
                                        <AvatarImage
                                            filePath={avatarPath}
                                            width={200}
                                            alt="avatar"
                                            style={{
                                                width: '342px',
                                                height: '229px',
                                                borderRadius: '20px',
                                                marginRight: '40px',
                                            }}
                                        />
                                    </div>
                                    <div className={classes.headerTextWrapper}>
                                        <div>
                                            <h1 className={classes.h1}>
                                                {profile?.institution
                                                    ? `Welcome, ${profile?.institution}!`
                                                    : 'Welcome!'}
                                            </h1>
                                            <MuiButton
                                                startIcon={
                                                    <EditIcon
                                                        className={
                                                            classes.editIcon
                                                        }
                                                    />
                                                }
                                                className={
                                                    classes.editProfileButton
                                                }
                                                onClick={() =>
                                                    setEditProfileDialogOpen(
                                                        true
                                                    )
                                                }
                                            >
                                                Edit Profile
                                            </MuiButton>
                                        </div>
                                        <div className={classes.buttons}>
                                            <Button
                                                icon={<AddIcon />}
                                                onClick={handleNewMemberClick}
                                            >
                                                New Member
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Paper>
                        </Grid>
                        <Grid item xs={4}>
                            <Paper
                                elevation={0}
                                style={{
                                    background: `linear-gradient(180deg, #CCECF9 0%, #D6E6FF 100%)`,
                                    borderRadius: '16px',
                                    padding: '16px 16px 0 16px',
                                    height: '100%',
                                }}
                            >
                                {monthlyMemberCounts && (
                                    <BarChart
                                        title="Monthly new members"
                                        xAxisData={monthlyMemberCounts.map(
                                            (count) =>
                                                new Date(
                                                    count.Date
                                                ).toLocaleString('default', {
                                                    month: 'short',
                                                })
                                        )}
                                        seriesData={monthlyMemberCounts.map(
                                            (count) => count.Count
                                        )}
                                    />
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={8}>
                            <Paper
                                elevation={0}
                                className={classes.eventsPaper}
                            >
                                <div className={classes.decorator} />
                                <div className={classes.eventsContent}>
                                    <h2 className={classes.h2}>Overview</h2>
                                    <Divider />
                                    <table className={classes.eventsTable}>
                                        <thead>
                                            <tr
                                                className={
                                                    classes.eventsTableHeader
                                                }
                                            >
                                                <th align="left">Event</th>
                                                <th align="right">Sample</th>
                                                <th align="right">Patient</th>
                                                <th align="right">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {latestEvents &&
                                                latestEvents.map((event) => (
                                                    <tr
                                                        className={
                                                            classes.eventsTableData
                                                        }
                                                        key={
                                                            event.Date.toString() +
                                                            event.SampleId
                                                        }
                                                    >
                                                        <CustomTooltip
                                                            title={event.Event.split(
                                                                /(?=[A-Z])/
                                                            )
                                                                .join(' ')
                                                                .replace(
                                                                    'Kit',
                                                                    'Test'
                                                                )}
                                                            placement="left"
                                                            variant="dark"
                                                        >
                                                            <td>
                                                                <TestName
                                                                    status={getStatusFromEvent(
                                                                        event
                                                                    )}
                                                                    variant="dashboard"
                                                                >
                                                                    {event.Event.split(
                                                                        /(?=[A-Z])/
                                                                    )
                                                                        .join(
                                                                            ' '
                                                                        )
                                                                        .replace(
                                                                            'Kit',
                                                                            'Test'
                                                                        )}
                                                                </TestName>
                                                            </td>
                                                        </CustomTooltip>
                                                        <td align="right">
                                                            {event.SampleId}
                                                        </td>
                                                        <CustomTooltip
                                                            title={
                                                                event.PatientId ||
                                                                ''
                                                            }
                                                            placement="right"
                                                            variant="dark"
                                                        >
                                                            <td
                                                                align="right"
                                                                className={
                                                                    classes.ellipsisCellWide
                                                                }
                                                            >
                                                                {
                                                                    event.PatientId
                                                                }
                                                            </td>
                                                        </CustomTooltip>
                                                        <td align="right">
                                                            {`${new Date(
                                                                event.Date
                                                            ).toLocaleDateString()} ${new Date(
                                                                event.Date
                                                            ).toLocaleTimeString()}`}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Paper>
                        </Grid>
                        <Grid item xs={4}>
                            <Paper
                                elevation={0}
                                style={{
                                    backgroundColor:
                                        theme.palette.primary.light,
                                    borderRadius: '16px',
                                    padding: '16px 16px 0 16px',
                                    height: '100%',
                                }}
                            >
                                {monthlyReleasedKitCounts && (
                                    <BarChart
                                        title="Monthly released tests"
                                        xAxisData={monthlyReleasedKitCounts.map(
                                            (count) =>
                                                new Date(
                                                    count.Date
                                                ).toLocaleString('default', {
                                                    month: 'short',
                                                })
                                        )}
                                        seriesData={monthlyReleasedKitCounts.map(
                                            (count) => count.Count
                                        )}
                                    />
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={4}>
                            <Paper
                                elevation={0}
                                className={classes.eventsPaper}
                            >
                                <div className={classes.eventsContent}>
                                    <div className={classes.decorator} />
                                    <h2 className={classes.h2}>
                                        Completed Tests
                                    </h2>
                                    <Divider />
                                    <table className={classes.eventsTable}>
                                        <thead>
                                            <tr
                                                className={
                                                    classes.eventsTableHeader
                                                }
                                            >
                                                <th align="left">Test Type</th>
                                                <th align="right">Sample</th>
                                                <th align="right">Patient</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {latestReleasedKits &&
                                                latestReleasedKits.map(
                                                    (kit) => (
                                                        <tr
                                                            className={
                                                                classes.eventsTableData
                                                            }
                                                            key={kit.SampleId}
                                                        >
                                                            <CustomTooltip
                                                                title={kit.Type}
                                                                placement="left"
                                                                variant="dark"
                                                            >
                                                                <td
                                                                    className={
                                                                        classes.eventsTableFirstColumn
                                                                    }
                                                                >
                                                                    {kit.Type}
                                                                </td>
                                                            </CustomTooltip>
                                                            <td align="right">
                                                                {kit.SampleId}
                                                            </td>
                                                            <CustomTooltip
                                                                title={
                                                                    kit.PatientId ||
                                                                    ''
                                                                }
                                                                placement="right"
                                                                variant="dark"
                                                            >
                                                                <td
                                                                    align="right"
                                                                    className={
                                                                        classes.ellipsisCell
                                                                    }
                                                                >
                                                                    {
                                                                        kit.PatientId
                                                                    }
                                                                </td>
                                                            </CustomTooltip>
                                                        </tr>
                                                    )
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </Paper>
                        </Grid>
                        <Grid item xs={4}>
                            <Paper
                                elevation={0}
                                className={classes.eventsPaper}
                            >
                                <div className={classes.decorator} />
                                <div className={classes.eventsContent}>
                                    <h2 className={classes.h2}>
                                        Pending Tests
                                    </h2>
                                    <Divider />
                                    <table className={classes.eventsTable}>
                                        <thead>
                                            <tr
                                                className={
                                                    classes.eventsTableHeader
                                                }
                                            >
                                                <th align="left">Test Type</th>
                                                <th align="right">Sample</th>
                                                <th align="right">Patient</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {latestPendingKits &&
                                                latestPendingKits.map((kit) => (
                                                    <tr
                                                        className={
                                                            classes.eventsTableData
                                                        }
                                                        key={kit.SampleId}
                                                    >
                                                        <CustomTooltip
                                                            title={kit.Type}
                                                            placement="left"
                                                            variant="dark"
                                                        >
                                                            <td
                                                                className={
                                                                    classes.eventsTableFirstColumn
                                                                }
                                                            >
                                                                {kit.Type}
                                                            </td>
                                                        </CustomTooltip>
                                                        <td align="right">
                                                            {kit.SampleId}
                                                        </td>
                                                        <CustomTooltip
                                                            title={
                                                                kit.PatientId ||
                                                                ''
                                                            }
                                                            placement="right"
                                                            variant="dark"
                                                        >
                                                            <td
                                                                align="right"
                                                                className={
                                                                    classes.ellipsisCell
                                                                }
                                                            >
                                                                {kit.PatientId}
                                                            </td>
                                                        </CustomTooltip>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Paper>
                        </Grid>
                        <Grid item xs={4}>
                            <Paper
                                elevation={0}
                                style={{
                                    background: '#FFFFFF',
                                    borderRadius: '16px',
                                    padding: '16px 16px 0 16px',
                                    height: '100%',
                                }}
                            >
                                {statusCounts && (
                                    <DoughnutChart
                                        title="Tests performance"
                                        seriesData={
                                            isNoTestPerformanceData
                                                ? []
                                                : ([
                                                      {
                                                          value: statusCounts.Ordered,
                                                          name: 'Ordered',
                                                          itemStyle: {
                                                              color: '#D6E6FF',
                                                          },
                                                      },
                                                      {
                                                          value: statusCounts.Released,
                                                          name: 'Released',
                                                          itemStyle: {
                                                              color: '#A3F3F5',
                                                          },
                                                      },
                                                      {
                                                          value: statusCounts.Pending,
                                                          name: 'Pending',
                                                          itemStyle: {
                                                              color: '#19255B',
                                                          },
                                                      },
                                                      {
                                                          value: statusCounts.Rejected,
                                                          name: 'Rejected',
                                                          itemStyle: {
                                                              color: '#FF9891',
                                                          },
                                                      },
                                                  ] as ChartDataItem[])
                                        }
                                        showLegend={!isNoTestPerformanceData}
                                    />
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </div>
            </div>
            <EditMemberForm
                open={newMemberDialogOpen}
                onClose={handleNewMemberDialogClose}
                isAddMode={true}
                onMemberUpdated={() => setMemberAdded(true)}
            />
            <EditProfileForm
                open={editProfileDialogOpen}
                onClose={() => setEditProfileDialogOpen(false)}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    wrapper: {
        position: 'relative',
        width: '100%',
        minHeight: '250px',
    },
    root: {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        padding: '40px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '32px',
        flex: '1 0 0',
        alignSelf: 'stretch',
    },
    backgroundImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '300px',
        zIndex: 0,
        background: `linear-gradient(0deg, rgba(39, 83, 108, 0.46) 0%, rgba(39, 83, 108, 0.46) 100%), url(${dashboardBackground}) center / cover no-repeat`,
    },
    grid: {
        marginTop: '20px',
    },
    header: {
        flexShrink: 0,
        borderRadius: '16px',
        backgroundColor: theme.palette.primary.light,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '20px',
        gap: '16px',
        height: '100%',
    },
    headerContainer: {
        display: 'flex',
    },
    headerImageContainer: {
        backgroundColor: '#27536C',
        width: '342px',
        height: '229px',
        borderRadius: '20px',
        marginRight: '40px',
    },
    headerTextWrapper: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flex: 1,
    },
    h1: {
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: '28px',
        fontWeight: 500,
        lineHeight: '120%',
        letterSpacing: '-0.56px',
        marginTop: '20px',
    },
    h2: {
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: '24px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '140%',
        letterSpacing: '0.12px',
        marginBottom: '10px',
        marginTop: '0px',
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    eventsPaper: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        borderRadius: '16px',
        height: '100%',
        paddingBottom: '20px',
        minHeight: '250px',
    },
    eventsContent: {
        flex: 1,
        paddingLeft: '40px',
        padding: '20px',
        maxHeight: '235px',
        overflow: 'auto',
    },
    decorator: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '16px',
        height: '100%',
        backgroundColor: theme.palette.primary.light,
        borderTopLeftRadius: '16px',
        borderBottomLeftRadius: '16px',
    },
    eventsTable: {
        width: '100%',
        marginTop: '10px',
    },
    eventsTableHeader: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
    },
    eventsTableFirstColumn: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        maxWidth: '180px',
    },
    ellipsisCellWide: {
        maxWidth: 250,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    ellipsisCell: {
        maxWidth: 100,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    eventsTableData: {
        color: theme.palette.gray.dark,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
    },
    editProfileButton: {
        display: 'flex',
        height: 32,
        width: 125,
        padding: '10px 14px',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 25,
        backgroundColor: '#EFF5FF',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: -0.252,
        textTransform: 'none',
        marginTop: '20px',
    },
    editIcon: {
        color: theme.palette.primary.main,
        scale: '0.8',
    },
}));

export default DashboardPage;
